import { floodFillOBB, looksLikeCircle, rotatePoint } from '../utils/geometry';

/**
 * Alpine component factory for the Filament resource-position form field.
 *
 * Usage from Blade:
 *
 *     <div x-data="{
 *         ...resourcePosition(@js([... initial values ...])),
 *         state: $wire.$entangle('{{ $getStatePath() }}'),
 *     }" ...>
 *
 * `state` lives on the spread component so `this.state = ...` flows back
 * through Livewire. Large/non-reactive state (the Leaflet map, the canvas
 * and its pixel buffer, the neighbour list, the hover guard) is kept in
 * closure scope — Alpine's reactive Proxy would otherwise wrap a
 * multi-megabyte Uint8ClampedArray on every read.
 */
export default function resourcePosition(config) {
    const defaults = {
        width: config.width,
        height: config.height,
        radius: config.radius,
        type: config.type,
        center: config.center,
    };
    const nonMovableItems = JSON.parse(JSON.stringify(config.nonMovableItems ?? []));

    let map = null;
    let item = null;
    let canvas = null;
    let pixelData = [];
    let disableClick = false;

    function loadCanvas(img) {
        canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, img.width, img.height);
        pixelData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    }

    return {
        width: config.width,
        height: config.height,
        radius: config.radius,
        type: config.type,
        center: config.center,
        angle: config.angle,
        position: config.position,
        mapLink: config.mapLink,
        detection: true,

        init() {
            this.initializeMap();

            this.$watch('type', () => this.updatePosition(this.center));
            this.$watch('height', () => this.updatePosition(this.center));
            this.$watch('width', () => this.updatePosition(this.center));
            this.$watch('radius', () => this.updatePosition(this.center));
            this.$watch('angle', () => this.updatePosition(this.center));
        },

        initializeMap() {
            if (!this.mapLink) {
                return;
            }
            const container = this.$refs.item;

            if (map) {
                map.remove();
                map = null;
            }

            if (container._leaflet_id != null) {
                container._leaflet_id = null;
            }

            map = L.map(container, {
                crs: L.CRS.Simple,
                minZoom: -3,
                maxZoom: 2,
            });

            const img = new Image();
            img.src = this.mapLink;
            img.crossOrigin = 'Anonymous';

            img.onload = () => {
                const bounds = [[0, 0], [img.height, img.width]];
                L.imageOverlay(img.src, bounds).addTo(map);
                map.setMaxBounds(bounds);
                map.fitBounds(bounds);

                loadCanvas(img);
                nonMovableItems.forEach((nm) => this.addNonClickableItem(nm));
                this.drawItem();
            };

            map.on('click', (e) => {
                const p = e.latlng;
                this.detectItem([Math.floor(p.lat), Math.floor(p.lng)]);
            });
        },

        detectItem(position) {
            if (disableClick) {
                return;
            }
            if (!this.detection) {
                this.updatePosition(position);
                return;
            }

            const start = { x: position[1], y: canvas.height - position[0] };
            const pixels = { data: pixelData, width: canvas.width, height: canvas.height };
            const obb = floodFillOBB(start, pixels);

            if (!obb) {
                this.updatePosition(position);
                return;
            }

            this.width = Math.floor(obb.halfWidth);
            this.height = Math.floor(obb.halfHeight);

            if (looksLikeCircle(obb)) {
                this.type = 'circle';
                this.radius = Math.round((obb.halfWidth + obb.halfHeight) / 2);
                this.angle = 0;
            } else {
                this.type = 'rectangle';
                this.radius = Math.max(this.width, this.height);
                this.angle = obb.degenerate ? 0 : Math.round(obb.angleDegrees);
            }

            this.updatePosition([
                Math.round(canvas.height - obb.center.y),
                Math.round(obb.center.x),
            ]);
        },

        updatePosition(pos) {
            this.center = pos;

            // Reject highlights that are far bigger than a plausible resource.
            if (this.radius > 200) {
                this.width = defaults.width;
                this.height = defaults.height;
                this.radius = defaults.radius;
                this.type = defaults.type;
                this.center = defaults.center;
                return;
            }

            this.position = {
                center: pos,
                height: parseInt(this.height),
                width: parseInt(this.width),
                radius: parseInt(this.radius),
                angle: parseInt(this.angle),
                type: this.type,
            };

            this.state = this.position;
            this.drawItem();
        },

        addNonClickableItem(position) {
            if (!position) {
                return;
            }

            if (position.type === 'circle') {
                L.circle(position.center, { color: 'blue', radius: position.radius, weight: 1 }).addTo(map);
                return;
            }

            const rectangle = L.rectangle([
                [position.center[0] + position.height, position.center[1] - position.width],
                [position.center[0] - position.height, position.center[1] + position.width],
            ], { color: 'blue', weight: 1 })
                .addTo(map)
                .on('mouseover', () => { disableClick = true; })
                .on('mouseout', () => { disableClick = false; });

            this.rotateRectangle(position, rectangle);
        },

        drawItem() {
            if (item) {
                item.remove();
            }
            if (!this.position || !this.position.center) {
                return;
            }

            if (this.position.type === 'circle') {
                item = L.circle(this.position.center, { color: 'green', radius: this.position.radius, weight: 1 }).addTo(map);
                return;
            }

            item = L.rectangle([
                [this.position.center[0] + this.position.height, this.position.center[1] - this.position.width],
                [this.position.center[0] - this.position.height, this.position.center[1] + this.position.width],
            ], { color: 'green', weight: 1 }).addTo(map);

            this.rotateRectangle(this.position, item);
        },

        rotateRectangle(position, rectangle) {
            if (!rectangle || !position) {
                return;
            }
            const center = rectangle.getBounds().getCenter();
            const rotationRadians = (position.angle * Math.PI) / 180;
            const rotatedCorners = rectangle.getLatLngs()[0].map((corner) => {
                const rotated = rotatePoint(corner, center, rotationRadians);
                return L.latLng(rotated.lat, rotated.lng);
            });
            rectangle.setLatLngs([rotatedCorners]);
        },
    };
}
